import React, { useEffect, useRef, useState } from 'react';
import fnc from '../../../mymod/commFunction';
import UsageContentsPanels from './UsageContentsPanels';
import UsageContentsUserSearch from './UsageContentsUserSearch';
import './UsageContents.scss';

const FIRST_AVAILABLE_MONTH = '2026-01';

function getCurrentMonthValue() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(monthValue, offset) {
    const [year, month] = monthValue.split('-').map(Number);
    const shifted = new Date(year, month - 1 + offset, 1);
    return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
}

function requestJson(url, data) {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error('timeout'));
        }, 15000);

        const request = fnc.sendAxios(url, data, (response) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            resolve(response);
        });

        Promise.resolve(request).then(() => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            reject(new Error('request-failed'));
        });
    });
}

const UsageContents = ({ searchName, onSearchNameChange }) => {
    const currentMonth = getCurrentMonthValue();
    const [candidates, setCandidates] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [monthValue, setMonthValue] = useState(currentMonth);
    const [rows, setRows] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('이름을 입력하고 회원을 검색해 주세요.');
    const [messageKind, setMessageKind] = useState('info');
    const searchRequestIdRef = useRef(0);
    const detailRequestIdRef = useRef(0);
    const searchFormRef = useRef(null);
    const initialSearchNameRef = useRef(String(searchName || '').trim());
    const restoredSearchRequestedRef = useRef(false);

    useEffect(() => {
        return () => {
            searchRequestIdRef.current += 1;
            detailRequestIdRef.current += 1;
        };
    }, []);

    useEffect(() => {
        if (restoredSearchRequestedRef.current || !initialSearchNameRef.current) return;
        restoredSearchRequestedRef.current = true;
        searchFormRef.current?.requestSubmit();
    }, []);

    async function loadUsageContents(user, selectedMonth = monthValue) {
        const requestId = detailRequestIdRef.current + 1;
        const [year, month] = selectedMonth.split('-').map(Number);
        detailRequestIdRef.current = requestId;
        setSelectedUser(user);
        setCandidates([]);
        setRows([]);
        setLoading(true);
        setMessage('사용내역을 조회하고 있습니다.');
        setMessageKind('loading');

        try {
            const response = await requestJson('/userRoutes/selectWalletUsageContents', {
                userId: user.user_id,
                stCode: user.stCode || '',
                year,
                month
            });

            if (detailRequestIdRef.current !== requestId) return;
            if (!Array.isArray(response)) throw new Error('invalid-response');

            setRows(response);
            setMessage(response.length > 0 ? '' : '조회된 사용내역이 없습니다.');
            setMessageKind('info');
        } catch (error) {
            if (detailRequestIdRef.current !== requestId) return;
            setMessage('사용내역 조회에 실패했습니다. 다시 시도해 주세요.');
            setMessageKind('error');
        } finally {
            if (detailRequestIdRef.current === requestId) setLoading(false);
        }
    }

    async function handleSearch(event) {
        event.preventDefault();
        const keyword = searchName.trim();

        if (!keyword) {
            setCandidates([]);
            setMessage('검색할 회원 이름을 입력해 주세요.');
            setMessageKind('error');
            return;
        }

        const requestId = searchRequestIdRef.current + 1;
        searchRequestIdRef.current = requestId;
        detailRequestIdRef.current += 1;
        setSearching(true);
        setLoading(false);
        setSelectedUser(null);
        setRows([]);
        setCandidates([]);
        setMessage('회원을 검색하고 있습니다.');
        setMessageKind('loading');

        try {
            const response = await requestJson('/userRoutes/selectWalletUsageUsers', {
                userName: keyword
            });

            if (searchRequestIdRef.current !== requestId) return;
            if (!Array.isArray(response)) throw new Error('invalid-response');

            if (response.length === 0) {
                setMessage(`'${keyword}' 이름으로 검색된 회원이 없습니다.`);
                setMessageKind('error');
                return;
            }

            if (response.length === 1) {
                setMessage('');
                setMessageKind('info');
                setSearching(false);
                loadUsageContents(response[0], monthValue);
                return;
            }

            setCandidates(response);
            setMessage('');
            setMessageKind('info');
        } catch (error) {
            if (searchRequestIdRef.current !== requestId) return;
            setMessage('회원 검색에 실패했습니다. 다시 시도해 주세요.');
            setMessageKind('error');
        } finally {
            if (searchRequestIdRef.current === requestId) setSearching(false);
        }
    }

    function handleMonthChange(nextMonth) {
        if (!nextMonth || nextMonth < FIRST_AVAILABLE_MONTH || nextMonth > currentMonth) return;
        setMonthValue(nextMonth);
        if (selectedUser) loadUsageContents(selectedUser, nextMonth);
    }

    function moveMonth(offset) {
        handleMonthChange(shiftMonth(monthValue, offset));
    }

    function handleReset() {
        searchRequestIdRef.current += 1;
        detailRequestIdRef.current += 1;
        onSearchNameChange('');
        setCandidates([]);
        setSelectedUser(null);
        setMonthValue(currentMonth);
        setRows([]);
        setSearching(false);
        setLoading(false);
        setMessage('이름을 입력하고 회원을 검색해 주세요.');
        setMessageKind('info');
    }

    return (
        <section className="usage-contents-frame" aria-labelledby="usage-contents-title">
            <header className="usage-contents-header">
                <div>
                    <strong id="usage-contents-title">사용내역 조회</strong>
                    <span>회원 이름으로 지갑 사용내용을 확인합니다.</span>
                </div>
            </header>

            <div className="usage-contents-controls">
                <UsageContentsUserSearch
                    value={searchName}
                    onChange={onSearchNameChange}
                    onSubmit={handleSearch}
                    searching={searching}
                    candidates={candidates}
                    onSelectUser={(user) => loadUsageContents(user, monthValue)}
                    formRef={searchFormRef}
                    onReset={handleReset}
                />

                {selectedUser && (
                    <div className="usage-contents-selected-row">
                        <div className="usage-contents-selected-user">
                            <span>조회 회원</span>
                            <strong>{selectedUser.user_name}</strong>
                            <small>{selectedUser.user_id} / {selectedUser.stCode || '코드 없음'} / {selectedUser.bill_id}</small>
                        </div>
                        <div className="usage-contents-month-controls">
                            <button
                                type="button"
                                onClick={() => moveMonth(-1)}
                                disabled={monthValue <= FIRST_AVAILABLE_MONTH || loading}
                            >
                                이전
                            </button>
                            <input
                                type="month"
                                min={FIRST_AVAILABLE_MONTH}
                                max={currentMonth}
                                value={monthValue}
                                onChange={(event) => handleMonthChange(event.target.value)}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={() => moveMonth(1)}
                                disabled={monthValue >= currentMonth || loading}
                            >
                                다음
                            </button>
                            <button
                                type="button"
                                onClick={() => handleMonthChange(currentMonth)}
                                disabled={monthValue === currentMonth || loading}
                            >
                                이번 달
                            </button>
                            <button
                                type="button"
                                className="usage-contents-refresh"
                                onClick={() => loadUsageContents(selectedUser, monthValue)}
                                disabled={loading}
                            >
                                새로고침
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {message && <div className={`usage-contents-message ${messageKind}`}>{message}</div>}
            {!loading && selectedUser && rows.length > 0 && (
                <UsageContentsPanels rows={rows} monthValue={monthValue} />
            )}
        </section>
    );
};

export default UsageContents;
